import { createContext, createElement, useContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BUSINESS_KIND_PROFILES, type BusinessKind } from '@repo/shared/business-kind';
import type { GbpScoreItemKey } from '@repo/shared/google-ranking';
import { useOrg } from '@/providers';
import { googleRankingProfileQueryOptions } from './use-google-ranking-profile-queries';

/**
 * Words the Google Ranking, Reviews and photo screens use for the salon's kind of business, so a pho
 * restaurant never reads "nail" or "salon". Built from the shared `BUSINESS_KIND_PROFILES`; nail salons keep
 * the exact words the screens always had.
 */
export interface BusinessKindCopy {
  kind: BusinessKind;
  /** "Nail salon", "Restaurant". */
  label: string;
  /** "salon", "restaurant", "business" — "your salon". */
  noun: string;
  /** "salons", "restaurants", "businesses" — "people comparing salons". */
  nounPlural: string;
  /** "technician", "stylist", "staff". */
  staffTerm: string;
  /** "busy nail salons" — who typical numbers stand for before the first map scan. */
  typicalLabel: string;
  /** "a busy nail salon" — the same, as one place. */
  typicalOne: string;
  /**
   * The search the main category is the strongest signal for ("nail salon"); null = no single right category
   * (OTHER), so the copy names no search.
   */
  categorySearch: string | null;
}

const NOUN_PLURAL: Record<BusinessKind, string> = { NAIL: 'salons', HAIR: 'salons', RESTAURANT: 'restaurants', OTHER: 'businesses' };

const TYPICAL_ONE: Record<BusinessKind, string> = {
  NAIL: 'a busy nail salon',
  HAIR: 'a busy hair salon',
  RESTAURANT: 'a busy restaurant',
  OTHER: 'a busy local business',
};

/** A kind this web build has words for; a newer API's unknown kind gets the generic wording, never nail's. */
function knownKind(kind: BusinessKind | null | undefined): BusinessKind {
  if (!kind) return 'NAIL';
  return Object.prototype.hasOwnProperty.call(BUSINESS_KIND_PROFILES, kind) ? kind : 'OTHER';
}

/** Copy for `kind`; no kind (loading, Google not usable, an older API) reads as nail, as the screens always did. */
export function businessKindCopy(kind: BusinessKind | null | undefined): BusinessKindCopy {
  const resolved = knownKind(kind);
  const profile = BUSINESS_KIND_PROFILES[resolved];
  return {
    kind: resolved,
    label: profile.label,
    noun: profile.noun,
    nounPlural: NOUN_PLURAL[resolved],
    staffTerm: profile.staffTerm,
    typicalLabel: profile.typicalLabel,
    typicalOne: TYPICAL_ONE[resolved],
    categorySearch: profile.canonicalPrimaryCategory?.displayName.toLowerCase() ?? null,
  };
}

/** Salons (nail, hair) call themselves "salon"; photo copy says "a salon photo" only for them. */
export const isSalon = (copy: Pick<BusinessKindCopy, 'noun'>) => copy.noun === 'salon';

/** Why an item is not scored for this kind of business (a greyed "n/a" row), one line. */
export function notApplicableReason(key: GbpScoreItemKey, copy: BusinessKindCopy): string {
  switch (key) {
    case 'main_category':
      return 'Google has no single right category for every kind of business, so this one isn’t scored.';
    case 'extra_categories':
      return 'Hayven scores extra categories against a Hayven service menu, and your business has none.';
    case 'services_coverage':
    case 'services_priced':
      return copy.kind === 'RESTAURANT' ? 'Restaurants can’t list services on Google.' : 'Your kind of business doesn’t list services on Google.';
    case 'booking_link':
      return 'Hayven can’t read reservation or order links yet.';
    default:
      return 'Not scored for your kind of business.';
  }
}

const BusinessKindCopyContext = createContext<BusinessKindCopy>(businessKindCopy('NAIL'));

/**
 * Gives the screens below it the copy of `kind`. The Google Ranking layout provides it from the profile it
 * already loads; Photo library (outside the layout) reads the kind with {@link useGoogleBusinessKind}, Reviews from its
 * summary.
 */
export function BusinessKindCopyProvider({ kind, children }: { kind: BusinessKind | null | undefined; children: ReactNode }) {
  const value = useMemo(() => businessKindCopy(kind), [kind]);
  return createElement(BusinessKindCopyContext.Provider, { value }, children);
}

/** Copy for the open salon's kind of business; nail wording outside a provider or while the kind loads. */
export function useBusinessKindCopy(): BusinessKindCopy {
  return useContext(BusinessKindCopyContext);
}

/**
 * The salon's kind of business (`businessKind` of the Google Ranking profile) for pages outside the Google
 * Ranking layout. Shares the profile's cache entry, so it is one database-only read, none when the layout
 * already loaded it. `kind` is null when Google is not usable (or the read failed); `pending` = still loading —
 * keep kind-worded copy off screen until then, so a restaurant never flashes nail words.
 */
export function useGoogleBusinessKind(): { kind: BusinessKind | null; pending: boolean } {
  const { orgId } = useOrg();
  const query = useQuery({ ...googleRankingProfileQueryOptions(orgId), select: (res) => res.data.businessKind });
  return { kind: query.data ?? null, pending: query.isPending };
}
